import React from 'react'
import { css } from '@emotion/css'
import { useTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
type StyleRules = Record<string, Record<string, any> | ((props?: any) => Record<string, any>)>
type StylesCreator = StyleRules | ((theme: Theme) => StyleRules)

export const createStyles = <T extends StyleRules>(styles: T): T => styles

export const makeStyles =
  (stylesOrCreator: StylesCreator) =>
  (props?: unknown) => {
    const theme = useTheme<Theme>()
    const styles =
      typeof stylesOrCreator === 'function'
        ? (stylesOrCreator as (theme: Theme) => StyleRules)(theme)
        : stylesOrCreator

    const classes: Record<string, string> = {}
    Object.keys(styles).forEach((key) => {
      const rule = styles[key]
      const resolved =
        typeof rule === 'function'
          ? (rule as (props?: unknown) => Record<string, any>)(props)
          : rule
      classes[key] = css(resolved as any)
    })
    return classes
  }

export const withStyles =
  (stylesOrCreator: StylesCreator) =>
  (Component: React.ComponentType<any>) => {
    const Wrapped = React.forwardRef<unknown, any>((props, ref) => {
      const theme = useTheme<Theme>()
      const styles =
        typeof stylesOrCreator === 'function'
          ? (stylesOrCreator as (theme: Theme) => StyleRules)(theme)
          : stylesOrCreator

      const classes: Record<string, string> = {}
      Object.keys(styles).forEach((key) => {
        const rule = styles[key]
        const resolved =
          typeof rule === 'function'
            ? (rule as (p?: unknown) => Record<string, any>)(props)
            : rule
        classes[key] = css(resolved as any)
      })

      return (
        <Component
          {...props}
          ref={ref}
          classes={{ ...(props.classes || {}), ...classes }}
        />
      )
    })

    Wrapped.displayName = `WithStyles(${Component.displayName || Component.name || 'Component'})`
    return Wrapped
  }
